import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Button from './Button';
import { Download, AlertCircle, FileText, Loader2 } from 'lucide-react';
import * as mammoth from 'mammoth';
import * as XLSX from 'xlsx';

export default function FilePreview({ isOpen, onClose, fileUrl }) {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fileType, setFileType] = useState(null);
    const [fileName, setFileName] = useState('');

    useEffect(() => {
        if (!isOpen || !fileUrl) {
            setContent(null);
            setFileType(null);
            return;
        }

        // 1. Determine File Type & Name
        const cleanUrl = fileUrl.split('?')[0].split('#')[0];
        const name = decodeURIComponent(cleanUrl.split('/').pop());
        setFileName(name);

        const ext = name.split('.').pop().toLowerCase();

        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
            setFileType('image');
            setLoading(false);
        } else if (ext === 'pdf') {
            setFileType('pdf');
            setLoading(false);
        } else if (['doc', 'docx'].includes(ext)) {
            setFileType('docx');
            fetchAndParseDocx(fileUrl);
        } else if (['xls', 'xlsx', 'csv'].includes(ext)) {
            setFileType('excel');
            fetchAndParseExcel(fileUrl);
        } else {
            setFileType('unknown');
            setLoading(false);
        }

    }, [isOpen, fileUrl]);

    // --- Parsers ---

    const fetchAndParseDocx = async (url) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(url);
            const arrayBuffer = await response.arrayBuffer();
            const result = await mammoth.convertToHtml({ arrayBuffer });
            setContent(result.value);
        } catch (err) {
            console.error("Docx Error:", err);
            setError("Не удалось отобразить документ Word. Скачайте файл для просмотра.");
        } finally {
            setLoading(false);
        }
    };

    const fetchAndParseExcel = async (url) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(url);
            const arrayBuffer = await response.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const html = XLSX.utils.sheet_to_html(sheet);
            setContent(html);
        } catch (err) {
            console.error("Excel Error:", err);
            setError("Не удалось отобразить таблицу Excel. Скачайте файл для просмотра.");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={fileName || 'Просмотр файла'} size="xl">
            <div className="flex flex-col h-[85vh]">

                {/* Error State */}
                {error && (
                    <div className="p-4 bg-red-50 text-red-700 flex items-center rounded-lg border border-red-100 mx-4 mt-4">
                        <AlertCircle className="w-5 h-5 mr-2" />
                        {error}
                    </div>
                )}

                {/* Content Container */}
                <div className="flex-1 overflow-y-auto p-4 bg-gray-50 rounded-lg relative min-h-[300px]">

                    {loading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 z-10">
                            <Loader2 className="w-10 h-10 text-primary-600 animate-spin mb-2" />
                            <span className="text-gray-500 font-medium">Загрузка документа...</span>
                        </div>
                    )}

                    {/* Image Viewer */}
                    {fileType === 'image' && (
                        <div className="flex items-center justify-center min-h-full">
                            <img src={fileUrl} alt="Preview" className="max-w-full max-h-full object-contain shadow-md rounded" />
                        </div>
                    )}

                    {/* PDF Viewer */}
                    {fileType === 'pdf' && (
                        <iframe
                            src={fileUrl}
                            title="PDF Preview"
                            className="w-full h-full rounded border border-gray-200"
                        />
                    )}

                    {/* DOCX Content */}
                    {fileType === 'docx' && content && (
                        <div
                            className="prose max-w-none bg-white p-8 shadow-sm text-gray-900"
                            dangerouslySetInnerHTML={{ __html: content }}
                        />
                    )}

                    {/* Excel Content */}
                    {fileType === 'excel' && content && (
                        <div
                            className="excel-preview bg-white p-4 shadow-sm overflow-auto"
                            dangerouslySetInnerHTML={{ __html: content }}
                        />
                    )}

                    {/* Unknown / Fallback */}
                    {fileType === 'unknown' && (
                        <div className="flex flex-col items-center justify-center h-full text-gray-500">
                            <FileText className="w-16 h-16 mb-4 text-gray-300" />
                            <p>Предпросмотр для этого типа файлов недоступен</p>
                        </div>
                    )}
                </div>

                {/* Footer / Actions */}
                <div className="p-4 border-t border-gray-100 bg-white flex justify-end rounded-b-lg">
                    <a href={fileUrl} download={fileName} target="_blank" rel="noopener noreferrer">
                        <Button variant="primary">
                            <Download className="w-4 h-4 mr-2" />
                            Скачать файл ({fileName})
                        </Button>
                    </a>
                </div>
            </div>
        </Modal>
    );
}
